/**
 * Payload embebido en mensajes tipo `archat_notice_v1` (prefijo + base64 UTF-8).
 * Contrato compartido con backend (MensajePostService).
 */
export const ARCHAT_I18N_V1_PREFIX = '__ARCHAT_I18N_V1__';

/** @param {unknown} s */
export function isArchatI18nV1BlobString(s) {
  return typeof s === 'string' && s.trim().startsWith(ARCHAT_I18N_V1_PREFIX);
}

/**
 * @param {string} blob
 * @returns {{ k: string, p: Record<string, string>, v: number[] } | null}
 */
export function decodeArchatI18nV1Payload(blob) {
  if (typeof blob !== 'string' || !blob.startsWith(ARCHAT_I18N_V1_PREFIX)) {
    return null;
  }
  const b64 = blob.slice(ARCHAT_I18N_V1_PREFIX.length);
  try {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) {
      bytes[i] = bin.charCodeAt(i);
    }
    const json = JSON.parse(new TextDecoder('utf-8').decode(bytes));
    if (!json || typeof json !== 'object') return null;
    const k = typeof json.k === 'string' ? json.k : '';
    const p = json.p && typeof json.p === 'object' && !Array.isArray(json.p) ? json.p : {};
    const rawV = Array.isArray(json.v)
      ? json.v
      : Array.isArray(json.visibleIds)
        ? json.visibleIds
        : [];
    const v = rawV.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
    return { k, p, v };
  } catch {
    return null;
  }
}

/**
 * @param {object} full
 * @param {object} raw
 * @param {number|string|null|undefined} currentUserId
 * @returns {{ mode: 'hidden' } | { mode: 'none' } | { mode: 'render', key: string, params: Record<string, string>, visibleTo: number[] }}
 */
export function resolveAttachmentRecipientFreeNotice(full, raw, currentUserId) {
  // Preferir fuentes que suelen conservar el blob canónico; `mensaje`/`text` pueden ser traducción WS/GET sin prefijo.
  const candidates = [
    full?.contenido_original,
    raw?.contenido_original,
    full?.mensaje,
    raw?.mensaje,
    full?.text,
    raw?.text,
    full?.display_text,
    raw?.display_text,
    full?.original_text,
    raw?.original_text,
  ];
  let blob = '';
  for (const x of candidates) {
    if (typeof x !== 'string') continue;
    const s = x.trim();
    if (s.startsWith(ARCHAT_I18N_V1_PREFIX)) {
      blob = s;
      break;
    }
  }
  if (!blob) {
    return { mode: 'none' };
  }
  const decoded = decodeArchatI18nV1Payload(blob);
  if (!decoded || decoded.k !== 'attachments.recipientFreeBlocked.receiver') {
    return { mode: 'none' };
  }
  const uid = Number(currentUserId);
  if (!Number.isFinite(uid) || uid <= 0) {
    return { mode: 'hidden' };
  }
  const visibleIds = decoded.v.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
  const uidStr = String(uid);
  if (!visibleIds.some((id) => id === uid || String(id) === uidStr)) {
    return { mode: 'hidden' };
  }
  const senderName = String(decoded.p?.senderName ?? '').trim() || '—';
  const planName = String(decoded.p?.planName ?? 'Free').trim() || 'Free';
  return {
    mode: 'render',
    key: decoded.k,
    params: { senderName, planName },
    visibleTo: decoded.v,
  };
}
